import { useEffect, useState } from 'react';
import BottomSheet from '../common/BottomSheet';
import ColorRow from '../common/ColorRow';
import IconPickerSheet from './IconPickerSheet';
import { useApp } from '../../context/AppContext';
import { COLOR_PALETTE } from '../../data/mockTemplates';
import { MAX_TEMPLATE_ICONS } from '../../constants/iconCatalog';

const NOTIFY_OPTIONS = ['通知しない', '30分前', '1時間前', '前日 20:00'];

/**
 * "テンプレート作成" / "テンプレート編集" bottom sheet — ひとつのフォーム
 * コンポーネントを追加・編集の両方で共用する（EventFormSheetと同じ
 * mode="add"|"edit"パターン）。AppContextの`templateForm`を直接読む
 * 自己完結型コンポーネントで、呼び出し側（App.jsx / TemplateList.jsx）
 * はopen/onCloseを個別に配線する必要がない。
 *
 *   null                         -> 閉じている
 *   { mode: 'add' }              -> 新規作成（空欄から開始）
 *   { mode: 'edit', template }   -> 編集（その テンプレートの内容で初期化）
 *
 * アイコンは単一選択ではなく「選択済みアイコンの配列」（0〜4個・選択順を
 * 維持）として保持する。入力欄自体は1つで、その中にチップとして並び、
 * ＋追加でIconPickerSheet（カテゴリ別一覧）を開く。各チップは✕で削除、
 * ‹ ›で並び替え可能（モバイルでの確実な操作性を優先し、ドラッグではなく
 * タップ式の並び替えを採用）。
 *
 * 保存されるicons配列は文字列配列のみ（例: ["👴","🏥"]）。
 */
export default function TemplateEditorSheet() {
  const { templateForm, closeTemplateForm, addTemplate, editTemplate, showToast } = useApp();

  const open = templateForm != null;
  const mode = templateForm?.mode ?? 'add';
  const editingTemplate = mode === 'edit' ? templateForm.template : null;

  const [icons, setIcons] = useState([]);
  const [name, setName] = useState('');
  const [place, setPlace] = useState('');
  const [memo, setMemo] = useState('');
  const [notify, setNotify] = useState('30分前');
  const [color, setColor] = useState(COLOR_PALETTE[1]);
  const [submitting, setSubmitting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // シートが開くたびに、編集なら対象テンプレートの内容で、追加なら空欄で初期化する。
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && editingTemplate) {
      const t = editingTemplate;
      setIcons(t.icons ? [...t.icons] : []);
      setName(t.name || '');
      setPlace(t.place || '');
      setMemo(t.memo || '');
      setNotify(t.notify || '30分前');
      setColor(t.color || COLOR_PALETTE[1]);
    } else {
      setIcons([]);
      setName('');
      setPlace('');
      setMemo('');
      setNotify('30分前');
      setColor(COLOR_PALETTE[1]);
    }
    setPickerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, editingTemplate]);

  const handleToggleIcon = (icon) => {
    setIcons((prev) => {
      if (prev.includes(icon)) return prev.filter((ic) => ic !== icon);
      if (prev.length >= MAX_TEMPLATE_ICONS) return prev; // guarded in the picker UI too
      return [...prev, icon];
    });
  };

  const handleRemoveIconAt = (index) => {
    setIcons((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMoveIcon = (index, direction) => {
    setIcons((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        icons,
        name: name.trim() || 'テンプレート名',
        place,
        memo,
        notify,
        color,
      };

      if (mode === 'edit') {
        await editTemplate(editingTemplate.id, payload);
        closeTemplateForm();
        showToast('テンプレートを更新しました');
      } else {
        await addTemplate(payload);
        closeTemplateForm();
        showToast('テンプレートを保存しました');
      }
      // editTemplate()/addTemplate() はuseTemplates内でローカル一覧を
      // 即時更新するので、一覧側は再読み込み不要で自動的に最新化される。
    } catch (err) {
      showToast(err?.message || '保存に失敗しました。もう一度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <BottomSheet
        open={open}
        onClose={closeTemplateForm}
        title={mode === 'edit' ? 'テンプレート編集' : 'テンプレート作成'}
        footer={
          <>
            <button className="btn btn-tonal" type="button" onClick={closeTemplateForm} disabled={submitting}>
              キャンセル
            </button>
            <button className="btn btn-filled" type="button" onClick={handleSave} disabled={submitting}>
              {submitting && <span className="btn-spinner" aria-hidden="true" />}
              {submitting ? '保存中…' : '保存'}
            </button>
          </>
        }
      >
        <div className="field">
          <label>アイコン（最大{MAX_TEMPLATE_ICONS}個）</label>
          <div className="icon-chip-row">
            {icons.map((ic, index) => (
              <div className="icon-chip" key={`${ic}-${index}`}>
                <span className="icon-chip-emoji">{ic}</span>
                <div className="icon-chip-controls">
                  <button
                    type="button"
                    className="icon-chip-move"
                    disabled={index === 0}
                    onClick={() => handleMoveIcon(index, -1)}
                    aria-label="左へ移動"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="icon-chip-move"
                    disabled={index === icons.length - 1}
                    onClick={() => handleMoveIcon(index, 1)}
                    aria-label="右へ移動"
                  >
                    ›
                  </button>
                  <button
                    type="button"
                    className="icon-chip-remove"
                    onClick={() => handleRemoveIconAt(index)}
                    aria-label="削除"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}

            {icons.length < MAX_TEMPLATE_ICONS && (
              <button type="button" className="icon-chip-add" onClick={() => setPickerOpen(true)}>
                ＋追加
              </button>
            )}
          </div>
        </div>

        <div className="field">
          <label>テンプレート名</label>
          <input type="text" placeholder="例）ピアノ教室" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="field">
          <label>場所</label>
          <input
            type="text"
            placeholder="例）〇〇音楽教室"
            value={place}
            onChange={(e) => setPlace(e.target.value)}
          />
        </div>

        <div className="field">
          <label>メモ</label>
          <textarea placeholder="持ち物など" value={memo} onChange={(e) => setMemo(e.target.value)} />
        </div>

        <div className="field">
          <label>通知</label>
          <select value={notify} onChange={(e) => setNotify(e.target.value)}>
            {NOTIFY_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>色</label>
          <ColorRow value={color} onChange={setColor} />
        </div>

        <div className="field">
          <label>プレビュー</label>
          <div className="preview-card" style={{ '--preview-color': color }}>
            <div className="p-icon">
              {icons.length > 0 ? icons.map((ic, i) => <span key={i}>{ic}</span>) : <span>📌</span>}
            </div>
            <div>
              <div className="p-name">{name || 'テンプレート名'}</div>
              <div className="p-meta">📍 {place || '未設定'}</div>
              <div className="p-meta">📝 {memo || '未設定'}</div>
            </div>
          </div>
        </div>
      </BottomSheet>

      <IconPickerSheet
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedIcons={icons}
        onToggle={handleToggleIcon}
      />
    </>
  );
}
